import { uiText } from '@/i18n/interface';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useEffectiveDojo } from './useEffectiveDojo';

export function useDashboardOverview() {
  const { effectiveDojoId } = useEffectiveDojo();
  const query = useQuery({
    queryKey: ['dashboard-overview', effectiveDojoId],
    enabled: !!effectiveDojoId,
    queryFn: async () => {
      if (!effectiveDojoId) throw new Error(uiText("Select a dojo."));
      // Supabase's row limit must not silently truncate an annual overview.
      async function pages<T>(read: (from: number, to: number) => PromiseLike<{ data: T[] | null; error: unknown }>): Promise<T[]> {
        const result: T[] = [];
        for (let from = 0; ; from += 500) {
          const { data, error } = await read(from, from + 499);
          if (error) throw error;
          result.push(...(data || []));
          if (!data || data.length < 500) return result;
        }
      }
      const sources = await Promise.allSettled([
        pages((from,to) => supabase.from('payment_instructions').select('*').eq('dojo_id', effectiveDojoId).order('id').range(from,to)),
        pages((from,to) => supabase.from('profiles').select('*').eq('dojo_id', effectiveDojoId).eq('role', 'student').order('id').range(from,to)),
        pages((from,to) => supabase.from('expenses').select('*').eq('dojo_id', effectiveDojoId).order('id').range(from,to)),
        pages((from,to) => supabase.from('payment_categories').select('*').eq('dojo_id', effectiveDojoId).order('id').range(from,to)),
      ]);
      const [paymentResult, studentResult, expenseResult, categoryResult] = sources;
      const message = (result: PromiseSettledResult<unknown>) => result.status === 'rejected'
        ? String(result.reason?.message || 'This data source could not be loaded.') : null;
      return {
        payments: paymentResult.status === 'fulfilled' ? paymentResult.value : [],
        students: studentResult.status === 'fulfilled' ? studentResult.value : [],
        expenses: expenseResult.status === 'fulfilled' ? expenseResult.value : [],
        categories: categoryResult.status === 'fulfilled' ? categoryResult.value : [],
        issues: { payments: message(paymentResult), students: message(studentResult), expenses: message(expenseResult), categories: message(categoryResult) },
      };
    },
    staleTime: 30_000,
    refetchOnMount: 'always',
  });
  return { ...query, hasDojo: !!effectiveDojoId };
}
